import { apiErrorMessage, customFetch } from '@agos/api-client'
import { T, formatCents, formatCount, tt } from '@agos/shared'
import { BUTTON_PRIMARY, ConfirmDialog, Spinner, cn, useToast } from '@agos/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, BellOff, ChefHat, HandCoins, MapPin, Phone, Undo2, Wallet } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import {
  BALANCE_KEY,
  BALANCE_PATH,
  MINE_KEY,
  MINE_PATH,
  POOL_KEY,
  POOL_PATH,
  POOL_POLL_MS,
  balanceNote,
  claimPath,
  courierBalanceOf,
  courierOrdersOf,
  offerLine,
  pickupNote,
  releasePath,
  settleNote,
  settlementNote,
  type CourierOrder,
} from './courier'
import {
  PushRefused,
  currentEnvironment,
  isSubscribed,
  pushNote,
  pushSupportOf,
  subscribeCourier,
  unsubscribeCourier,
} from './push'

/**
 * The external courier's part of `/driving` - `FRONTEND.md` §9.786, owner O139.
 *
 * Three tabs: ***Čaká na šoféra*** (the pool, with the street, distance, reward and pickup time, and *Vziať*),
 * ***Moje*** (what he has and has not yet carried out - the customer, *Vrátiť do ponuky* - with the page's own
 * delivery cards under it for what is on the road), and ***Zárobok*** (today's rewards and what is owed between
 * him and the restaurant). Plus the switch that brings the push to this phone.
 */
export type CourierTab = 'pool' | 'mine' | 'earnings'

export function CourierPanels({
  tab,
  onTab,
  onTheRoad,
}: {
  tab: CourierTab
  onTab: (tab: CourierTab) => void
  /** How many orders he is carrying now - the page's own delivery list, counted on *Moje*. */
  onTheRoad: number
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [releasing, setReleasing] = useState<CourierOrder | null>(null)

  const pool = useQuery({
    queryKey: POOL_KEY,
    queryFn: () => customFetch<unknown>(POOL_PATH),
    refetchInterval: POOL_POLL_MS,
  })
  const mine = useQuery({
    queryKey: MINE_KEY,
    queryFn: () => customFetch<unknown>(MINE_PATH),
    refetchInterval: POOL_POLL_MS,
  })
  const balance = useQuery({
    queryKey: BALANCE_KEY,
    queryFn: () => customFetch<unknown>(BALANCE_PATH),
    enabled: tab === 'earnings',
  })
  const offered = courierOrdersOf(pool.data)
  const waiting = courierOrdersOf(mine.data)
  const owed = courierBalanceOf(balance.data)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: POOL_KEY })
    void queryClient.invalidateQueries({ queryKey: MINE_KEY })
    void queryClient.invalidateQueries({ queryKey: BALANCE_KEY })
  }

  const claim = useMutation({
    mutationFn: (orderId: number) => customFetch<unknown>(claimPath(orderId), { method: 'POST' }),
    onSuccess: () => {
      refresh()
      toast({ title: tt('Objednávka je vaša'), description: tt('Prevádzka vie, že pre ňu prídete.') })
      onTab('mine')
    },
    onError: (caught) => {
      refresh()
      toast({
        variant: 'destructive',
        title: tt('Objednávku sa nepodarilo vziať'),
        description: apiErrorMessage(caught, tt('Skúste to znova.')),
      })
    },
  })

  const release = useMutation({
    mutationFn: (orderId: number) => customFetch<unknown>(releasePath(orderId), { method: 'POST' }),
    onSuccess: () => {
      setReleasing(null)
      refresh()
      toast({ title: tt('Objednávka je späť v ponuke') })
    },
    onError: (caught) =>
      toast({
        variant: 'destructive',
        title: tt('Objednávku sa nepodarilo vrátiť'),
        description: apiErrorMessage(caught, tt('Skúste to znova.')),
      }),
  })

  const tabs: { key: CourierTab; label: string; count: number | null }[] = [
    { key: 'pool', label: tt('Čaká na šoféra'), count: offered === null ? null : offered.length },
    { key: 'mine', label: tt('Moje'), count: waiting === null ? null : waiting.length + onTheRoad },
    { key: 'earnings', label: tt('Zárobok'), count: null },
  ]

  return (
    <section className="space-y-3" data-courier>
      <CourierPushSwitch />

      <div className="grid grid-cols-3 gap-1 rounded-2xl bg-muted p-1" role="tablist" data-courier-tabs>
        {tabs.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            data-courier-tab={entry.key}
            onClick={() => onTab(entry.key)}
            className={cn(
              'min-h-[44px] rounded-xl text-sm font-bold flex items-center justify-center gap-1.5',
              tab === entry.key ? 'bg-card shadow-sm' : 'text-muted-foreground',
            )}
          >
            <span className="truncate">{entry.label}</span>
            {entry.count !== null && entry.count > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] leading-5">
                {formatCount(entry.count)}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'pool' && (
        <div className="space-y-2" data-courier-pool>
          {pool.isLoading ? (
            <Spinner />
          ) : offered === null ? (
            <Failed what={tt('Ponuku objednávok sa nepodarilo načítať.')} onRetry={() => void pool.refetch()} />
          ) : offered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-10" data-courier-pool-empty>
              <T>Žiadna objednávka teraz nečaká na šoféra. Keď príde, ozve sa telefón.</T>
            </p>
          ) : (
            offered.map((order) => (
              <OrderCard key={order.id} order={order} marker="data-pool-order">
                <button
                  type="button"
                  data-claim={order.id}
                  disabled={claim.isPending}
                  onClick={() => claim.mutate(order.id)}
                  className={cn('w-full min-h-[48px] rounded-2xl font-bold disabled:opacity-50', BUTTON_PRIMARY)}
                >
                  <T>Vziať</T>
                </button>
              </OrderCard>
            ))
          )}
        </div>
      )}

      {tab === 'mine' && (
        <div className="space-y-2" data-courier-mine>
          {mine.isLoading ? (
            <Spinner />
          ) : waiting === null ? (
            <Failed what={tt('Vaše objednávky sa nepodarilo načítať.')} onRetry={() => void mine.refetch()} />
          ) : (
            waiting.map((order) => (
              <OrderCard key={order.id} order={order} marker="data-mine-order">
                {order.customerName !== null && (
                  <p className="text-sm font-semibold" data-mine-customer>
                    {order.customerName}
                  </p>
                )}
                {order.customerPhone !== null && (
                  <a
                    href={`tel:${order.customerPhone}`}
                    className="text-sm text-primary flex items-center gap-1.5"
                    data-mine-phone
                  >
                    <Phone className="w-4 h-4" /> {order.customerPhone}
                  </a>
                )}
                {order.notes !== null && <p className="text-xs text-muted-foreground">{order.notes}</p>}
                <button
                  type="button"
                  data-release={order.id}
                  onClick={() => setReleasing(order)}
                  className="w-full min-h-[44px] rounded-2xl border border-border text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <Undo2 className="w-4 h-4" /> <T>Vrátiť do ponuky</T>
                </button>
              </OrderCard>
            ))
          )}
          {waiting !== null && waiting.length === 0 && onTheRoad === 0 && (
            <p className="text-center text-sm text-muted-foreground py-10" data-courier-mine-empty>
              <T>Nemáte žiadnu objednávku. Vezmite si ju v Čaká na šoféra.</T>
            </p>
          )}
        </div>
      )}

      {tab === 'earnings' && (
        <div className="space-y-3" data-courier-earnings>
          {balance.isLoading ? (
            <Spinner />
          ) : owed === null ? (
            <Failed what={tt('Zárobok sa nepodarilo načítať.')} onRetry={() => void balance.refetch()} />
          ) : (
            <>
              <div className="rounded-2xl border border-border bg-card p-4 space-y-1" data-courier-balance={owed.owesCents}>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Wallet className="w-4 h-4" /> <T>S prevádzkou</T>
                </p>
                <p className={cn('text-2xl font-bold', owed.owesCents < 0 ? 'text-emerald-500' : '')}>
                  {balanceNote(owed.owesCents)}
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-4 space-y-2" data-courier-today>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <HandCoins className="w-4 h-4" /> <T>Dnes ste zarobili</T>
                </p>
                <p className="text-2xl font-bold text-emerald-500" data-courier-today-rewards>
                  {formatCents(owed.todayRewardsCents)}
                </p>
                {owed.today.map((order) => (
                  <div key={order.id} className="flex items-center justify-between text-sm" data-courier-today-order={order.id}>
                    <span>#{order.orderNumber}</span>
                    <span className="text-muted-foreground">
                      {order.cashCents > 0 && `${tt('hotovosť')} ${formatCents(order.cashCents)} · `}
                      {tt('odmena')} {formatCents(order.rewardCents)}
                    </span>
                  </div>
                ))}
                {owed.todaySettlements.map((settlement) => (
                  <p key={settlement.id} className="text-xs text-muted-foreground" data-courier-settlement={settlement.id}>
                    {settlementNote(settlement)}
                    {settlement.note !== null && ` · ${settlement.note}`}
                  </p>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        open={releasing !== null}
        onClose={() => setReleasing(null)}
        onConfirm={() => {
          if (releasing !== null) release.mutate(releasing.id)
        }}
        title={tt('Vrátiť objednávku do ponuky?')}
        description={tt('Uvidia ju ostatní šoféri a môže si ju vziať niekto iný.')}
        confirmLabel={tt('Vrátiť')}
        loading={release.isPending}
      />
    </section>
  )
}

function OrderCard({
  order,
  marker,
  children,
}: {
  order: CourierOrder
  marker: 'data-pool-order' | 'data-mine-order'
  children: ReactNode
}) {
  return (
    <article className="rounded-2xl border border-border bg-card p-4 space-y-2" {...{ [marker]: order.id }}>
      <div className="flex items-start justify-between gap-3">
        <p className="font-bold">#{order.orderNumber}</p>
        {order.rewardCents !== null && (
          <p className="text-lg font-bold text-emerald-500" data-order-reward>
            {formatCents(order.rewardCents)}
          </p>
        )}
      </div>
      <p className="text-sm flex items-start gap-1.5">
        <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-primary" /> {order.address}
      </p>
      <p className="text-xs text-muted-foreground" data-order-offer>
        {offerLine(order)}
      </p>
      <p className="text-sm flex items-center gap-1.5" data-order-pickup={order.pickup.state}>
        <ChefHat className="w-4 h-4 text-muted-foreground" /> {pickupNote(order.pickup)}
      </p>
      <p className="text-sm font-semibold" data-order-settle={order.settleCents}>
        {settleNote(order)}
      </p>
      {children}
    </article>
  )
}

function Failed({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-4 space-y-2" role="alert" data-courier-failed>
      <p className="text-sm">{what}</p>
      <button type="button" onClick={onRetry} className="text-sm font-semibold text-primary">
        <T>Skúsiť znova</T>
      </button>
    </div>
  )
}

/** The switch that brings the push to this phone, with the plain truth when it cannot. */
function CourierPushSwitch() {
  const { toast } = useToast()
  const support = pushSupportOf(currentEnvironment())
  const [subscribed, setSubscribed] = useState(false)
  const [busy, setBusy] = useState(false)
  const permission = typeof Notification === 'undefined' ? 'default' : Notification.permission

  useEffect(() => {
    let alive = true
    void isSubscribed().then((answer) => {
      if (alive) setSubscribed(answer)
    })
    return () => {
      alive = false
    }
  }, [])

  const toggle = async () => {
    setBusy(true)
    try {
      if (subscribed) {
        await unsubscribeCourier()
        setSubscribed(false)
      } else {
        await subscribeCourier()
        setSubscribed(true)
        toast({ title: tt('Upozornenia sú zapnuté') })
      }
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Upozornenia sa nepodarilo zapnúť'),
        description: caught instanceof PushRefused ? caught.message : apiErrorMessage(caught, tt('Skúste to znova.')),
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-3 space-y-1.5" data-courier-push={subscribed ? 'on' : 'off'}>
      {support === 'supported' && (
        <button
          type="button"
          data-courier-push-toggle
          disabled={busy}
          onClick={() => void toggle()}
          className={cn(
            'w-full min-h-[44px] rounded-xl font-bold flex items-center justify-center gap-2 border-2 disabled:opacity-50',
            subscribed ? 'border-emerald-500/40 text-emerald-500' : 'border-border text-muted-foreground',
          )}
        >
          {subscribed ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
          {subscribed ? tt('Upozornenia na nové objednávky: zapnuté') : tt('Zapnúť upozornenia na nové objednávky')}
        </button>
      )}
      <p className="text-[11px] text-muted-foreground" data-courier-push-note>
        {pushNote(support, permission, subscribed)}
      </p>
    </div>
  )
}
